"use client"

import { motion } from "motion/react"

import {
  CloudNavy,
  LightningNavy,
  RainbowNavy,
  StarOneNavy,
  StarThreeNavy,
  StarTwoNavy,
  SunNavy,
} from "@/assets/images/illustrations"
import { Illustration } from "@/components/ui/illustration"
import { cn } from "@/lib/utils"

/**
 * The SplitBackgroundEffect component.
 * This component renders the background effect for the auth split layout.
 * It includes a bloom effect and a illustrations.
 * @returns The SplitBackgroundEffect component.
 */
export const SplitBackgroundEffect = () => (
  <>
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 z-[-1]"
    >
      {ILLUSTRATIONS.map(({ src, position, size, float, ...illustration }) => (
        <motion.div
          key={position}
          className={cn("absolute", position)}
          animate={{ y: [0, -float.y, 0], rotate: [0, float.rotate, 0] }}
          transition={{
            duration: float.duration,
            delay: float.delay,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          <Illustration {...illustration} src={src} className={size} />
        </motion.div>
      ))}
    </div>
  </>
)

/**
 * The interface for the illustration fragment props.
 * src - Illustration asset
 * viewBox - SVG viewBox
 * intrinsicWidth - SVG intrinsic width
 * intrinsicHeight - SVG intrinsic height
 * position - Absolute placement just outside the form card, so the
 *   illustrations bracket it as a loose frame (Tailwind classes)
 * size - Size and opacity classes for the illustration
 * float - Gentle drift to keep the frame alive: vertical travel in px, tilt
 *   sway in deg, loop duration and stagger in s
 */
interface IllustrationProp {
  src: string
  viewBox: string
  intrinsicWidth: number
  intrinsicHeight: number
  position: string
  size: string
  float: { y: number; rotate: number; duration: number; delay: number }
}

/*
 * Constants of illustrations for decorating the background effect.
 */
const STAR = {
  viewBox: "0 0 834 834",
  intrinsicWidth: 834,
  intrinsicHeight: 834,
}
const WIDE = {
  viewBox: "0 0 3126 2918",
  intrinsicWidth: 3126,
  intrinsicHeight: 2918,
}

// Positions are relative to the form card (inset-0). Generous negative offsets
// push each illustration well clear of the card so the frame breathes; the
// distances and heights are deliberately uneven (no two sides mirror) and each
// piece carries a small tilt, so the whole thing reads hand-placed rather than
// snapped to a grid. Larger shapes anchor the corners, small stars dot the gaps.
const ILLUSTRATIONS: IllustrationProp[] = [
  // ── Top edge ─────────────────────────────────────────────────────────────
  {
    src: CloudNavy,
    ...WIDE,
    position: "-top-16 -left-20",
    size: "h-12 w-auto opacity-15 xl:h-18",
    float: { y: 9, rotate: -6, duration: 8, delay: 0.6 },
  },
  {
    src: SunNavy,
    ...WIDE,
    position: "-top-24 -right-14",
    size: "h-16 w-auto opacity-15 xl:h-24",
    float: { y: 10, rotate: 5, duration: 7, delay: 0 },
  },
  // ── Left edge ────────────────────────────────────────────────────────────
  {
    src: LightningNavy,
    ...WIDE,
    position: "top-20 -left-24",
    size: "h-10 w-auto opacity-15 xl:h-12",
    float: { y: 5, rotate: -9, duration: 4.5, delay: 1.1 },
  },
  {
    src: StarTwoNavy,
    ...STAR,
    position: "bottom-20 -left-14",
    size: "h-7 w-auto opacity-15 xl:h-9",
    float: { y: 6, rotate: 8, duration: 6, delay: 0.9 },
  },
  // ── Right edge ───────────────────────────────────────────────────────────
  {
    src: StarOneNavy,
    ...STAR,
    position: "top-32 -right-20",
    size: "h-10 w-auto opacity-15 xl:h-12",
    float: { y: 8, rotate: -7, duration: 5, delay: 0.4 },
  },
  {
    src: CloudNavy,
    ...WIDE,
    position: "bottom-32 -right-24",
    size: "h-12 w-auto opacity-15 xl:h-16",
    float: { y: 4, rotate: 6, duration: 5, delay: 1.9 },
  },
  // ── Bottom edge ──────────────────────────────────────────────────────────
  {
    src: RainbowNavy,
    ...WIDE,
    position: "-bottom-16 -left-10",
    size: "h-20 w-auto opacity-15 xl:h-24",
    float: { y: 7, rotate: 3, duration: 9, delay: 1.6 },
  },
  {
    src: StarThreeNavy,
    ...STAR,
    position: "-bottom-12 -right-16",
    size: "h-8 w-auto opacity-15 xl:h-10",
    float: { y: 9, rotate: -5, duration: 5.5, delay: 1.3 },
  },
]
